/**
 * Dinheiro.
 *
 * Regra do projeto: valores monetários trafegam e são persistidos SEMPRE em
 * centavos, como inteiro. Nada de float — 0.1 + 0.2 !== 0.3 e um erro de
 * arredondamento em desconto de plano vira divergência de caixa.
 *
 * A conversão para/de decimal acontece apenas em duas fronteiras:
 *   - exibição na UI (formatBRL);
 *   - integração com o Mercado Pago, que espera `transaction_amount` decimal.
 */

export type Cents = number;

export class InvalidAmountError extends Error {
  constructor(value: unknown) {
    super(`Valor monetário inválido: ${String(value)}`);
    this.name = 'InvalidAmountError';
  }
}

/** Garante que o valor é um inteiro de centavos não negativo. */
export function assertCents(value: number): Cents {
  if (!Number.isInteger(value) || value < 0 || !Number.isFinite(value)) {
    throw new InvalidAmountError(value);
  }
  return value;
}

/**
 * Converte "129,90" | "1.299,90" | "129.90" | 129.9 em centavos.
 *
 * O ponto é ambíguo: em "1.299,90" é separador de milhar, em "129.90" é
 * separador decimal. Desempatamos pela presença da vírgula — se ela existe, ela
 * é o separador decimal e todo ponto é milhar; se não existe, o último ponto é
 * o separador decimal.
 */
export function toCents(value: string | number): Cents {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) throw new InvalidAmountError(value);
    return Math.round(value * 100);
  }

  const cleaned = value.trim().replace(/\s|R\$/g, '');
  if (!cleaned) throw new InvalidAmountError(value);

  const normalized = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;

  if (!/^\d+(\.\d+)?$/.test(normalized)) throw new InvalidAmountError(value);

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) throw new InvalidAmountError(value);
  return Math.round(parsed * 100);
}

/**
 * Converte centavos no decimal que o Mercado Pago espera em transaction_amount.
 * 12990 → 129.9
 */
export function centsToDecimal(cents: Cents): number {
  assertCents(cents);
  return Math.round(cents) / 100;
}

/** 12990 → "R$ 129,90" */
export function formatBRL(cents: Cents, locale = 'pt-BR', currency = 'BRL'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    centsToDecimal(assertCents(cents)),
  );
}

/**
 * Aplica desconto percentual arredondando a favor do cliente (para baixo no
 * valor final), com meio-para-cima no centavo. 10% de 12990 → 1299 de desconto.
 */
export function applyPercentDiscount(cents: Cents, percent: number): Cents {
  assertCents(cents);
  if (percent < 0 || percent > 100) throw new InvalidAmountError(percent);
  const discount = Math.round((cents * percent) / 100);
  return Math.max(0, cents - discount);
}

/** Desconto de valor fixo, nunca deixando o total negativo. */
export function applyFixedDiscount(cents: Cents, discountCents: Cents): Cents {
  assertCents(cents);
  assertCents(discountCents);
  return Math.max(0, cents - discountCents);
}

/** Soma segura de centavos. */
export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce<Cents>((acc, v) => acc + assertCents(v), 0);
}

/**
 * O valor de um atendimento: o principal com o desconto do plano, mais os
 * encaixes. O desconto nunca incide no encaixe — por isso a soma vem depois.
 * Toda tela e todo relatório usam esta conta; uma cópia dela esquecendo o
 * encaixe mostraria dois "totais" diferentes para o mesmo horário.
 */
export function appointmentTotalCents(a: {
  readonly priceCents: number;
  readonly discountCents: number;
  readonly addOnsCents?: number | null;
}): number {
  return a.priceCents - a.discountCents + (a.addOnsCents ?? 0);
}

/**
 * O que falta pagar no balcão: o total menos o que já foi pago adiantado
 * (PIX ou crédito do cliente). Nunca negativo.
 */
export function appointmentDueCents(a: {
  readonly priceCents: number;
  readonly discountCents: number;
  readonly addOnsCents?: number | null;
  readonly paidCents?: number | null;
}): number {
  return Math.max(0, appointmentTotalCents(a) - (a.paidCents ?? 0));
}
